import { useState } from 'react';
import { BarChart3, Plus } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Tabs } from '@/components/ui/Tabs';
import { toast } from '@/hooks/useToast';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { cn } from '@/lib/utils';
import { CONSOLE_PALETTE } from '@/features/institute/theme';
import {
  CLASS_DISTRIBUTION,
  DRIVER_SUMMARY,
  FLEET_STANDING,
  ROUTE_SUMMARY,
  STUDENT_TRANSPORT_SUMMARY,
  TRACKING_ALERTS,
  TRACKING_SUMMARY,
  TODAYS_TRIPS,
  TRACKED_VEHICLES,
  TRANSPORT_ALERTS,
  TRANSPORT_DOCUMENTS,
  TRANSPORT_DRIVERS,
  TRANSPORT_ROUTES,
  TRANSPORT_STUDENTS,
  TRANSPORT_SUMMARY,
  TRANSPORT_VEHICLES,
  UPCOMING_SERVICES,
  VEHICLE_SUMMARY,
} from '../mock/transport.mock';
import { DriversSideRail, DRIVER_ACTION_ICONS } from '../components/DriversSideRail';
import { DriversTab } from '../components/DriversTab';
import { ActiveVehiclesPanel } from '../components/ActiveVehiclesPanel';
import { LiveMapPanel } from '../components/LiveMapPanel';
import { LiveTrackingPanel } from '../components/LiveTrackingPanel';
import { RoutesSideRail, ROUTE_ACTION_ICONS } from '../components/RoutesSideRail';
import { RoutesTab } from '../components/RoutesTab';
import { StudentsSideRail, STUDENT_ACTION_ICONS } from '../components/StudentsSideRail';
import { StudentsTab } from '../components/StudentsTab';
import { TransportSideRail, QUICK_ACTION_ICONS } from '../components/TransportSideRail';
import {
  TransportStatRow,
  DRIVER_TILES,
  ROUTE_TILES,
  STUDENT_TILES,
  TRACKING_TILES,
  TRANSPORT_TILES,
  VEHICLE_TILES,
} from '../components/TransportStatRow';
import { VehiclesSideRail, VEHICLE_ACTION_ICONS } from '../components/VehiclesSideRail';
import { VehiclesTab } from '../components/VehiclesTab';
import { TripsPanel } from '../components/TripsPanel';
import { TrackingBoard } from '../components/TrackingBoard';
import { TrackingSideRail } from '../components/TrackingSideRail';

type TransportTab = 'dashboard' | 'routes' | 'vehicles' | 'drivers' | 'students' | 'tracking';

/**
 * School transport: the fleet, the routes, who drives them and who rides them.
 *
 * **Static, deliberately.** Every figure on this screen comes from
 * `../mock/transport.mock.ts` — there is no transport module on the server yet.
 * The mock is shaped as `@/types/transport`, which is written as the response
 * the API will return, so wiring it up means adding `api/transport.api.ts`,
 * replacing the one import block above with `useApiQuery` calls, and touching
 * nothing else in this feature.
 *
 * What is *not* faked: every control works. The tabs switch, the trip list
 * filters and pages, each register searches, and the quick actions report
 * honestly that they are waiting on the endpoint rather than pretending to have
 * saved something. A screen that answers nothing cannot be judged against the
 * design it was drawn from.
 *
 * The console's palette is on the page root, so the shared fields, tables and
 * badges inside it paint themselves in the institute's blue rather than in the
 * product's default teal (CLAUDE.md §7).
 */
const TABS = [
  { value: 'dashboard' as const, label: 'Dashboard' },
  { value: 'routes' as const, label: 'Routes' },
  { value: 'vehicles' as const, label: 'Vehicles' },
  { value: 'drivers' as const, label: 'Drivers' },
  { value: 'students' as const, label: 'Students' },
  { value: 'tracking' as const, label: 'Tracking' },
];

/**
 * What the page says it is about, per tab.
 *
 * The heading stays "Transport" — the address did not change and neither did
 * the section — but the sentence under it names the job in hand, so a reader
 * landing on Routes is not told about vehicles and drivers they cannot see.
 */
/** What the header's primary button offers, per tab. */
const PRIMARY_ACTION: Record<TransportTab, { label: string; noun: string }> = {
  dashboard: { label: 'Add route', noun: 'route' },
  routes: { label: 'Add route', noun: 'route' },
  vehicles: { label: 'Add vehicle', noun: 'vehicle' },
  drivers: { label: 'Add driver', noun: 'driver' },
  students: { label: 'Assign student', noun: 'student to a route' },
  tracking: { label: 'Add route / mapping', noun: 'route mapping' },
};

const SUBTITLE: Record<TransportTab, string> = {
  dashboard: "Manage your school's transport operations — vehicles, routes, drivers and students.",
  routes: 'Manage and monitor all transport routes.',
  vehicles: 'Manage and monitor all school transport vehicles.',
  drivers: 'Manage and monitor all school transport drivers.',
  students: 'Manage and monitor all students using transport.',
  tracking: 'Real-time location of every vehicle on the road.',
};

/**
 * The open tab, kept in the address so Back from anything opened on it returns
 * to the same tab rather than to the dashboard. Each tab's register keeps its
 * own filters under its own prefix.
 */
type Filters = {
  tab: TransportTab;
};

const DEFAULT_FILTERS: Filters = {
  tab: 'dashboard',
};

const FILTER_OPTIONS = {
  allowed: {
    tab: TABS.map((option) => option.value),
  },
};

export function TransportPage() {
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const { tab } = filters;
  const setTab = (next: TransportTab) => setFilters({ tab: next });
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);

  /**
   * What every action on this screen does until the module exists.
   *
   * Said out loud rather than silently doing nothing: a button that swallows a
   * click is how a screen ends up believed to work when it does not — which is
   * the whole reason this section is being built static-first.
   */
  function notYet(what: string) {
    toast.info(`${what} is not connected yet`, 'The transport module is still being built.');
  }

  const quickActions = [
    { key: 'route', label: 'Add route', icon: QUICK_ACTION_ICONS.addRoute, onSelect: () => notYet('Adding a route') },
    { key: 'vehicle', label: 'Add vehicle', icon: QUICK_ACTION_ICONS.addVehicle, onSelect: () => notYet('Adding a vehicle') },
    { key: 'driver', label: 'Add driver', icon: QUICK_ACTION_ICONS.addDriver, onSelect: () => notYet('Adding a driver') },
    { key: 'assign', label: 'Assign students', icon: QUICK_ACTION_ICONS.assignStudents, onSelect: () => notYet('Assigning students') },
    { key: 'reports', label: 'Transport reports', icon: QUICK_ACTION_ICONS.reports, onSelect: () => notYet('Transport reports') },
    { key: 'tracking', label: 'Tracking', icon: QUICK_ACTION_ICONS.tracking, onSelect: () => setTab('tracking') },
  ];

  const routeActions = [
    { key: 'add', label: 'Add route', icon: ROUTE_ACTION_ICONS.addRoute, onSelect: () => notYet('Adding a route') },
    { key: 'vehicle', label: 'Assign vehicle', icon: ROUTE_ACTION_ICONS.assignVehicle, onSelect: () => notYet('Assigning a vehicle') },
    { key: 'driver', label: 'Assign driver', icon: ROUTE_ACTION_ICONS.assignDriver, onSelect: () => notYet('Assigning a driver') },
    { key: 'stops', label: 'Manage stops', icon: ROUTE_ACTION_ICONS.manageStops, onSelect: () => notYet('Managing stops') },
    { key: 'reports', label: 'Route reports', icon: ROUTE_ACTION_ICONS.routeReports, onSelect: () => notYet('Route reports') },
    { key: 'export', label: 'Export routes', icon: ROUTE_ACTION_ICONS.exportRoutes, onSelect: () => notYet('Exporting routes') },
  ];

  const vehicleActions = [
    { key: 'add', label: 'Add vehicle', icon: VEHICLE_ACTION_ICONS.addVehicle, onSelect: () => notYet('Adding a vehicle') },
    { key: 'driver', label: 'Assign driver', icon: VEHICLE_ACTION_ICONS.assignDriver, onSelect: () => notYet('Assigning a driver') },
    { key: 'report', label: 'Vehicle report', icon: VEHICLE_ACTION_ICONS.vehicleReport, onSelect: () => notYet('The vehicle report') },
    { key: 'log', label: 'Maintenance log', icon: VEHICLE_ACTION_ICONS.maintenanceLog, onSelect: () => notYet('The maintenance log') },
    { key: 'schedule', label: 'Service schedule', icon: VEHICLE_ACTION_ICONS.serviceSchedule, onSelect: () => notYet('The service schedule') },
  ];

  const driverActions = [
    { key: 'add', label: 'Add driver', icon: DRIVER_ACTION_ICONS.addDriver, onSelect: () => notYet('Adding a driver') },
    { key: 'report', label: 'Driver report', icon: DRIVER_ACTION_ICONS.driverReport, onSelect: () => notYet('The driver report') },
    { key: 'licence', label: 'Licence report', icon: DRIVER_ACTION_ICONS.licenceReport, onSelect: () => notYet('The licence report') },
    { key: 'route', label: 'Assign route', icon: DRIVER_ACTION_ICONS.assignRoute, onSelect: () => notYet('Assigning a route') },
  ];

  const studentActions = [
    { key: 'add', label: 'Add student', icon: STUDENT_ACTION_ICONS.addStudent, onSelect: () => notYet('Adding a student to transport') },
    { key: 'route', label: 'Assign route', icon: STUDENT_ACTION_ICONS.assignRoute, onSelect: () => notYet('Assigning a route') },
    { key: 'stops', label: 'Manage stops', icon: STUDENT_ACTION_ICONS.manageStops, onSelect: () => notYet('Managing stops') },
    { key: 'report', label: 'Student report', icon: STUDENT_ACTION_ICONS.studentReport, onSelect: () => notYet('The student report') },
    { key: 'pass', label: 'Transport pass', icon: STUDENT_ACTION_ICONS.transportPass, onSelect: () => notYet('Transport passes') },
    { key: 'parent', label: 'Parent contact', icon: STUDENT_ACTION_ICONS.parentContact, onSelect: () => notYet('Parent contacts') },
  ];

  return (
    <div className={cn('flex flex-col gap-5', CONSOLE_PALETTE)}>
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-[700] tracking-tight text-ink">Transport</h1>
          <p className="mt-1 text-sm text-ink-2">
            {SUBTITLE[tab]}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="secondary"
            size="md"
            leftIcon={<BarChart3 className="h-4 w-4" />}
            onClick={() => notYet('Transport reports')}
          >
            Reports
          </Button>
          {/* The primary action names what this tab is about, so the button
              nearest the reader is the one they came to press. */}
          <Button
            size="md"
            className="text-sm font-bold"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => notYet(`Adding a ${PRIMARY_ACTION[tab].noun}`)}
          >
            {PRIMARY_ACTION[tab].label}
          </Button>
        </div>
      </header>

      {tab === 'routes' ? (
        <TransportStatRow tiles={ROUTE_TILES} summary={ROUTE_SUMMARY} />
      ) : tab === 'vehicles' ? (
        <TransportStatRow tiles={VEHICLE_TILES} summary={VEHICLE_SUMMARY} />
      ) : tab === 'drivers' ? (
        <TransportStatRow tiles={DRIVER_TILES} summary={DRIVER_SUMMARY} />
      ) : tab === 'students' ? (
        <TransportStatRow tiles={STUDENT_TILES} summary={STUDENT_TRANSPORT_SUMMARY} />
      ) : tab === 'tracking' ? (
        <TransportStatRow tiles={TRACKING_TILES} summary={TRACKING_SUMMARY} />
      ) : (
        <TransportStatRow tiles={TRANSPORT_TILES} summary={TRANSPORT_SUMMARY} />
      )}

      {/* The strip carries its own bottom rule, which is the divider between it
          and the tab's content — so it is not boxed in a card of its own. `pt-1`
          is the room the focus ring needs; without it the ring is clipped by the
          row above. */}
      <Tabs
        options={TABS}
        value={tab}
        onChange={setTab}
        label="Transport views"
        className="pt-1"
      />

      {tab === 'dashboard' && (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-12 w-full max-w-full">
          <div className="flex flex-col gap-5 xl:col-span-8 2xl:col-span-9 min-w-0">
            <LiveTrackingPanel
              vehicles={TRACKED_VEHICLES}
              selectedVehicleUuid={selectedVehicle}
              onSelectVehicle={(vehicle) => setSelectedVehicle(vehicle.uuid)}
              onOpenFullMap={() => setTab('tracking')}
            />
            <TripsPanel
              trips={TODAYS_TRIPS}
              routes={TRANSPORT_ROUTES}
              onOpenTrip={(trip) => notYet(`Opening ${trip.routeName}`)}
            />
          </div>

          <div className="xl:col-span-4 2xl:col-span-3 min-w-0">
            <TransportSideRail
              standing={FLEET_STANDING}
              alerts={TRANSPORT_ALERTS}
              documents={TRANSPORT_DOCUMENTS}
              actions={quickActions}
              onViewReport={() => notYet('Transport reports')}
              onViewAllAlerts={() => notYet('The notification log')}
              onViewAllDocuments={() => notYet('The document library')}
              onDownloadDocument={(document) => notYet(`Downloading ${document.name}`)}
            />
          </div>
        </div>
      )}

      {tab === 'routes' && (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-12 w-full max-w-full">
          <div className="xl:col-span-8 2xl:col-span-9 min-w-0">
            <RoutesTab
              routes={TRANSPORT_ROUTES}
              onOpenRoute={(route) => notYet(`Opening ${route.name}`)}
              onEditRoute={(route) => notYet(`Editing ${route.name}`)}
              onRouteMenu={(route) => notYet(`More actions for ${route.name}`)}
            />
          </div>

          <div className="xl:col-span-4 2xl:col-span-3 min-w-0">
            <RoutesSideRail
              summary={ROUTE_SUMMARY}
              actions={routeActions}
              onViewRouteReport={() => notYet('The route report')}
              onViewDistanceReport={() => notYet('The distance report')}
            />
          </div>
        </div>
      )}
      {tab === 'vehicles' && (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-12 w-full max-w-full">
          <div className="xl:col-span-8 2xl:col-span-9 min-w-0">
            <VehiclesTab
              vehicles={TRANSPORT_VEHICLES}
              onOpenVehicle={(vehicle) => notYet(`Opening ${vehicle.registrationNumber}`)}
              onEditVehicle={(vehicle) => notYet(`Editing ${vehicle.registrationNumber}`)}
              onVehicleMenu={(vehicle) => notYet(`More actions for ${vehicle.registrationNumber}`)}
            />
          </div>

          <div className="xl:col-span-4 2xl:col-span-3 min-w-0">
            <VehiclesSideRail
              summary={VEHICLE_SUMMARY}
              services={UPCOMING_SERVICES}
              actions={vehicleActions}
              onViewStatusReport={() => notYet('The vehicle status report')}
              onViewComplianceReport={() => notYet('The fitness compliance report')}
              onViewAllServices={() => notYet('The service schedule')}
            />
          </div>
        </div>
      )}
      {tab === 'drivers' && (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-12 w-full max-w-full">
          <div className="xl:col-span-8 2xl:col-span-9 min-w-0">
            <DriversTab
              drivers={TRANSPORT_DRIVERS}
              onOpenDriver={(driver) => notYet(`Opening ${driver.fullName}`)}
              onEditDriver={(driver) => notYet(`Editing ${driver.fullName}`)}
              onDriverMenu={(driver) => notYet(`More actions for ${driver.fullName}`)}
            />
          </div>

          <div className="xl:col-span-4 2xl:col-span-3 min-w-0">
            <DriversSideRail
              summary={DRIVER_SUMMARY}
              actions={driverActions}
              onViewStatusReport={() => notYet('The driver status report')}
              onViewLicenceReport={() => notYet('The licence report')}
            />
          </div>
        </div>
      )}
      {tab === 'students' && (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-12 w-full max-w-full">
          <div className="xl:col-span-8 2xl:col-span-9 min-w-0">
            <StudentsTab
              students={TRANSPORT_STUDENTS}
              onOpenStudent={(student) => notYet(`Opening ${student.fullName}`)}
              onEditStudent={(student) => notYet(`Editing ${student.fullName}`)}
              onStudentMenu={(student) => notYet(`More actions for ${student.fullName}`)}
              onExport={(students) =>
                notYet(`Exporting ${students.length} student${students.length === 1 ? '' : 's'}`)
              }
            />
          </div>

          <div className="xl:col-span-4 2xl:col-span-3 min-w-0">
            <StudentsSideRail
              summary={STUDENT_TRANSPORT_SUMMARY}
              distribution={CLASS_DISTRIBUTION}
              actions={studentActions}
              onViewStudentsReport={() => notYet('The students report')}
              onViewClassReport={() => notYet('The class-wise report')}
            />
          </div>
        </div>
      )}
      {tab === 'tracking' && (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-12 w-full max-w-full">
          <div className="flex flex-col gap-5 xl:col-span-8 2xl:col-span-9 min-w-0">
            <LiveMapPanel
              vehicles={TRACKED_VEHICLES}
              routes={TRANSPORT_ROUTES}
              onExpand={() => notYet('The full-screen map')}
            />
            <ActiveVehiclesPanel
              vehicles={TRACKED_VEHICLES}
              onOpenVehicle={(vehicle) => notYet(`Opening ${vehicle.registrationNumber}`)}
              onLocateVehicle={(vehicle) => {
                setSelectedVehicle(vehicle.uuid);
                notYet(`Centring the map on ${vehicle.registrationNumber}`);
              }}
            />
            {/* How far along each run is — the one thing neither the map nor
                the table above says. */}
            <TrackingBoard vehicles={TRACKED_VEHICLES} />
          </div>

          <div className="xl:col-span-4 2xl:col-span-3 min-w-0">
            <TrackingSideRail
              vehicles={TRACKED_VEHICLES}
              alerts={TRACKING_ALERTS}
              onViewAllVehicles={() => notYet('The full vehicle list')}
              onViewAllAlerts={() => notYet('The alert log')}
              onVehicleMenu={(vehicle) => notYet(`More actions for ${vehicle.registrationNumber}`)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
